// combatpro/panels/spells/SpellSlot.js
//
// Combat PRO - SpellSlot (functional migration of the spell system).
//
// Reproduces a single spell-row from the spells-block template in
// battletracker.html. One slot contains the labels row (Spell N, Cast Round,
// Rounds Left, Actions), the controls row (spell select, cast-round input,
// rounds badge, Clear / Edit / State buttons with the Edit tooltip), and the
// per-slot spell-info and spell-exec mounts (SpellInfo / SpellExec).
//
// Interpolation and handlers are converted faithfully:
//   - IDs are computed from index/spellIdx exactly as the template did
//     (spell{spellIdx}-{index}, castRound..., roundsLeft..., stateBtn...).
//   - Inline style strings become JSX style objects (same declarations).
//   - The cast-round input used value="1"; it is rendered as defaultValue="1"
//     so it stays uncontrolled (no state) with the same initial DOM value.
//   - Existing handlers are converted to received props and forwarded only,
//     matching the original call signatures:
//       updateSpell(index, spellIdx, value)          -> onSpellChange
//       updateSpellCastRound(index, spellIdx, int)   -> onCastRoundChange
//       clearSpell(index, spellIdx)                  -> onClearSpell
//       vi_editSpellFromSlot(index, spellIdx)        -> onEditSpell
//     The State button has no onclick in the source, so none is added here.
//   - Em dashes in the source (rounds badge text and the state value &#x2014;)
//     are rendered via {'\u2014'} to match output without a literal em dash.
//
// Imports React, the SpellInfo / SpellExec child mounts, and CombatContext (read
// only, for the controlled select value). No business logic or handler
// implementations beyond the forwarded props. Do not edit battletracker.html.
//
// Stage 48B-4 (Spell Dropdown Population): the spell <select> previously rendered
// only "-- None --". It now renders the spell options from the existing loaded
// spell database (window.VI_SPELLS_DB - the same source SpellExec reads), and is
// controlled by the slot's stored spell name. The legacy populated this select
// imperatively (vi_rebuildSpellDropdownsForMonster / vi_rebuildSpellSelects ->
// vi_buildSortedSpellOptions, battletracker.html 4217-4266) via innerHTML /
// insertAdjacentHTML; spellEngine.buildSortedSpellOptions is the migrated form of
// that helper but returns an HTML string, so it is NOT consumed here. Instead the
// grouping + sort BEHAVIOUR is translated into declarative <optgroup>/<option>
// JSX (buildSpellOptionGroups below). No HTML strings, no DOM, no new database.
//
// Legacy filtering note: vi_rebuildSpellDropdownsForMonster filtered to a
// monster's known-spell STRING list when present, else the full DB. The React
// monster.spells are the three active slot OBJECTS ({ name, castRound }), never a
// known-spell string list, so the legacy predicate (typeof s === "string") is
// always false for this shape and resolves to the full-DB branch. The dropdown is
// therefore populated from the full window.VI_SPELLS_DB, which is the faithful
// translation for the current data shape (no known-spell pool exists to filter).
//
// No optional chaining. No em dash.

import React, { useContext, useMemo } from 'react';
import { CombatContext } from '../../contexts/CombatContext';
import { chooseBestSpellForMonster } from '../../spell/spellAiEngine';
import { getSpellData } from '../../spell/spellEngine';
import { trackDuration } from '../../spell/spellCastEngine';
import SpellInfo from './SpellInfo';
import SpellExec from './SpellExec';

// Declarative translation of vi_buildSortedSpellOptions (battletracker.html
// 4217-4240). Groups records by school (trimmed; empty -> "Other"), sorts group
// names alphabetically, and within each group sorts by level ascending (null ->
// 999) then name. Returns plain data ([{ school, spells }]) for the component to
// render as elements; it builds NO HTML and touches NO DOM. Reads name/school/
// level - the exact fields the legacy builder read off SPELLS_DB.
function buildSpellOptionGroups(spellsDb) {
  const resolved = (spellsDb || []).filter(Boolean);
  const groups = {};
  resolved.forEach(s => {
    const key = (s.school && s.school.trim()) ? s.school.trim() : 'Other';
    if (!groups[key]) { groups[key] = []; }
    groups[key].push(s);
  });
  const sortedKeys = Object.keys(groups).sort((a, b) => a.localeCompare(b));
  sortedKeys.forEach(key => {
    groups[key].sort((a, b) => {
      const levelA = Number(a.level != null ? a.level : 999);
      const levelB = Number(b.level != null ? b.level : 999);
      if (levelA !== levelB) { return levelA - levelB; }
      return String(a.name || '').localeCompare(String(b.name || ''));
    });
  });
  return sortedKeys.map(key => ({ school: key, spells: groups[key] }));
}

function SpellSlot(props) {
  const index = props.index;
  const spellIdx = props.spellIdx;
  const { state } = useContext(CombatContext);

  // Stage 48B-4: controlled value = the spell name stored for this slot, so the
  // dropdown reflects runtime state (including programmatic assignment such as
  // Random Spell) and preserves the selection across re-renders - the React
  // equivalent of the legacy currentVal restore (battletracker.html 4253).
  const monster = state.monsters[index];
  const slot = (monster && monster.spells) ? monster.spells[spellIdx] : null;
  const selectedSpellName = (slot && slot.name) ? slot.name : '';

  // Stage 48B-4: option source = the existing loaded spell database, read exactly
  // as SpellExec reads it. No duplicate database, no embedded spell list.
  const spellsDb = (typeof window !== 'undefined' && window.VI_SPELLS_DB) ? window.VI_SPELLS_DB : [];

  // Rounds Left + State (parity restoration). The modular port hardcoded these to
  // an em dash / inactive. They are established: derived from the selected spell's
  // duration via trackDuration (the migrated pure fn) with the slot's cast round
  // and the current combat round. No new runtime and no cast event is required. An
  // instantaneous spell (durationRounds 0) reads Instant -> the em dash and an
  // Inactive state (as the good-state screenshots show); a lasting spell with
  // rounds remaining reads Active with the number of rounds left.
  const spellRecord = getSpellData(selectedSpellName, spellsDb);
  const slotCastRound = (slot && slot.castRound != null) ? slot.castRound : 1;
  const roundsValue = spellRecord ? trackDuration(spellRecord, slotCastRound, state.currentRound) : null;
  const roundsIsInstant = roundsValue === 'Instant';
  const roundsLeftText = (roundsValue === null || roundsIsInstant) ? '\u2014' : String(roundsValue);
  const spellActive = !!spellRecord && !roundsIsInstant && typeof roundsValue === 'number' && roundsValue > 0;
  const stateClass = spellActive ? 'vi-state-btn vi-state-active' : 'vi-state-btn vi-state-inactive';
  const stateValueText = !spellRecord ? '\u2014' : (spellActive ? 'Active' : 'Inactive');
  const optionGroups = buildSpellOptionGroups(spellsDb);

  // Stage 49-3 (Recommendation Badge): consume the completed AI runtime to show
  // the legacy RECOMMENDED badge. The recommendation is the monster's single best
  // spell (chooseBestSpellForMonster); a row earns the badge when its assigned
  // spell name matches that best spell, mirroring the legacy check
  // (battletracker.html 7627-7634: vi_chooseBestSpellForMonster(index) then
  // aiResult.spell.name === spellName). All AI logic stays in the locked runtime;
  // nothing is duplicated or cached here.
  //
  // chooseBestSpellForMonster depends only on the monster list, the players, and
  // the spell database - not on which slot is rendering - so it is memoised on
  // exactly those inputs. This recomputes when the combat / spell-selection state
  // changes (state.monsters / state.players are replaced on every CombatContext
  // update) and does NOT recompute on unrelated re-renders. It is a per-render
  // memo, not persistent state and not a parallel recommendation cache.
  const recommended = useMemo(
    () => chooseBestSpellForMonster(index, state.monsters, state.players, spellsDb),
    [index, state.monsters, state.players, spellsDb]
  );
  const isRecommended = !!(recommended && recommended.spell && recommended.spell.name === selectedSpellName);

  return (
    <div className={isRecommended ? 'spell-row vi-spell-recommended' : 'spell-row'} style={isRecommended ? { position: 'relative' } : undefined}>
      <div style={{ width: '100%' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr auto', columnGap: '12px', alignItems: 'center' }}>
          <label style={{ fontSize: '14px', fontWeight: 700, color: '#FFFFFF', letterSpacing: '0.5px' }}>Spell {spellIdx + 1}</label>
          <label style={{ fontSize: '14px', fontWeight: 700, color: '#FFFFFF', letterSpacing: '0.5px', whiteSpace: 'nowrap', textAlign: 'center', display: 'block' }}>Cast Round</label>
          <label style={{ fontSize: '14px', fontWeight: 700, color: '#FFFFFF', letterSpacing: '0.5px', whiteSpace: 'nowrap', textAlign: 'center', display: 'block' }}>Rounds Left</label>
          <label style={{ fontSize: '14px', fontWeight: 700, color: '#FFFFFF', letterSpacing: '0.5px', textAlign: 'center', display: 'block' }}>Actions</label>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr auto', columnGap: '12px', alignItems: 'center', marginTop: '6px' }}>
          <select id={`spell${spellIdx}-${index}`} data-role="spell-select" value={selectedSpellName} onChange={(e) => props.onSpellChange(index, spellIdx, e.target.value)} style={{ width: '100%', height: '38px', lineHeight: '38px', fontSize: '14px', padding: '0 12px', boxSizing: 'border-box' }}>
            <option value="">-- None --</option>
            {optionGroups.map(g => (
              <optgroup key={g.school} label={g.school}>
                {g.spells.map((s, i) => (
                  <option key={g.school + '-' + i} value={s.name}>{'[Lv ' + (s.level != null ? s.level : '?') + '] ' + s.name}</option>
                ))}
              </optgroup>
            ))}
          </select>
          <input type="number" className="small-input" id={`castRound${spellIdx}-${index}`} defaultValue="1" style={{ width: '100%', height: '38px', lineHeight: '38px', fontSize: '14px', padding: '0 12px', boxSizing: 'border-box' }} onChange={(e) => props.onCastRoundChange(index, spellIdx, parseInt(e.target.value))} />
          <div className={spellActive ? 'rounds-badge vi-rounds-active' : 'rounds-badge'} id={`roundsLeft${spellIdx}-${index}`} style={{ width: '100%', height: '38px', lineHeight: '38px', fontSize: '14px', padding: '0 12px', boxSizing: 'border-box', textAlign: 'center' }}>{roundsLeftText}</div>
          <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
            <button className="btn-danger" style={{ height: '38px', lineHeight: '38px', padding: '0 12px', fontSize: '14px', boxSizing: 'border-box', whiteSpace: 'nowrap', minWidth: '90px' }} onClick={() => props.onClearSpell(index, spellIdx)}>Clear</button>
            <div className="vi-tooltip-wrap"><button className="vi-btn vi-btn-secondary" style={{ height: '38px', padding: '0 10px', fontSize: '13px', whiteSpace: 'nowrap' }} onClick={() => props.onEditSpell(index, spellIdx)}>✏️ Edit</button><div className="vi-tooltip"><div className="vi-tooltip-title">Edit Active Spell Setup</div><div className="vi-tooltip-body">Modify the currently assigned active spells for this monster.<br /><br />• Replace spells using the dropdown selector<br />• Adjust selections without resetting the encounter<br />• Changes apply instantly to this monster only</div><div className="vi-tooltip-footer">Use this to fine-tune abilities during combat</div></div></div>
            <button className={stateClass} id={`stateBtn${spellIdx}-${index}`}><span className="vi-state-label">State:</span> <span className="vi-state-value">{stateValueText}</span></button>
          </div>
        </div>
        <SpellInfo index={index} spellIdx={spellIdx} />
        <SpellExec index={index} spellIdx={spellIdx} />
      </div>
      {isRecommended ? <span className="vi-rec-badge">RECOMMENDED</span> : null}
    </div>
  );
}

export default SpellSlot;
